import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ICHING_TRIGRAMS } from '../../core/iching-trigrams.const';
import { IChingCoinFace, IChingMethodContent, IChingReading } from '../../core/iching.models';
import { injectIChingTranslate } from '../iching-consult-translate';
import { IChingMovingRow, buildMovingRows } from './iching-reading-derivations';

/**
 * De bewegende lijnen (C2): evenwicht (hoeveel), concentratie (waar), per
 * lijn plaats, rol, Drie Machten, boodschap en de Lai Zhide-waarschuwing
 * (yang 9 of yin 6), plus de samenvattende trigrammen bij 3 tot 5 lijnen.
 */
@Component({
  selector: 'hd-iching-moving-lines',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host { display: block; font-size: 13.5px; line-height: 1.5; }
    .origin-hint { margin: 0 0 10px; font-size: 12.5px; opacity: 0.85; }
    .badges { display: flex; flex-wrap: wrap; gap: 6px 8px; margin-bottom: 10px; }
    /* Eigen klassenaam: de globale .badge zet witte tekst. */
    .iching-badge {
      padding: 2px 10px; border-radius: 999px; font-size: 12.5px;
      color: var(--text-primary, #2b2b2b);
      border: 1px solid var(--separator, rgba(0, 0, 0, 0.15));
      background: var(--amber-10, rgba(212, 168, 105, 0.1));
    }
    .iching-badge--balance { border-color: var(--primary, #3c4650); font-weight: 600; }
    .rows { display: flex; flex-direction: column; gap: 10px; }
    .iching-moving__row {
      display: grid; grid-template-columns: 32px 1fr; gap: 4px 10px;
      padding: 10px 12px; border-radius: var(--radius-md, 6px);
      background: var(--hd-iching-card-bg, rgba(0, 0, 0, 0.035));
    }
    .position {
      grid-row: span 4; align-self: start; display: flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border-radius: 50%; font-weight: 700;
      background: var(--primary, #3c4650); color: var(--hd-iching-on-primary, #fff);
    }
    .iching-moving__row > :not(.position) { grid-column: 2; }
    .title { font-weight: 600; }
    .origin { font-size: 12.5px; }
    .meta { font-size: 12.5px; opacity: 0.8; }
    p { margin: 0; }
    .warning {
      padding: 6px 10px; border-left: 2px solid var(--hd-iching-warning, #c0763a);
      background: var(--hd-iching-warning-bg, rgba(192, 118, 58, 0.08));
    }
    .warning-symbol { margin-right: 6px; font-size: 16px; }
    .summary { margin-top: 10px; }
    .summary-symbol { margin-left: 6px; font-size: 18px; }
    .hint { margin-top: 8px; font-size: 12.5px; font-style: italic; opacity: 0.8; }
  `,
  template: `
    <p class="origin-hint" data-test-id="iching-moving-origin-hint">{{ t('humanDesign.iching.reading.movingOrigin') }}</p>

    <div class="badges">
      <span class="iching-badge iching-badge--balance" data-test-id="iching-balance">{{ t('humanDesign.iching.reading.balance.' + reading().balance) }}</span>
      @if (reading().concentration !== 'none') {
        <span class="iching-badge">{{ t('humanDesign.iching.reading.concentration.' + reading().concentration) }}</span>
      }
    </div>

    @if (rows().length > 0) {
      <div class="rows">
        @for (row of rows(); track row.position) {
          <div class="iching-moving__row" [attr.data-position]="row.position">
            <span class="position">{{ row.position }}</span>
            @if (row.method; as m) {
              <span class="title">{{ m.title }}</span>
              <span class="meta">{{ m.role }} · {{ t('humanDesign.iching.reading.sancai.' + m.sancai) }}</span>
            }
            <p class="origin" data-test-id="iching-moving-origin">{{ row.origin }}</p>
            @if (row.method; as m) {
              <p>{{ m.movingMessage }}</p>
            }
            @if (row.warning) {
              <p class="warning">
                @if (row.symbol) {
                  <span class="warning-symbol" aria-hidden="true">{{ row.symbol }}</span>
                }
                {{ row.warning }}
              </p>
            }
          </div>
        }
      </div>
    }

    @if (summarySymbols().length > 0) {
      <p class="summary" data-test-id="iching-summary-trigrams">
        {{ t('humanDesign.iching.reading.summaryTrigrams') }}
        @for (symbol of summarySymbols(); track $index) {
          <span class="summary-symbol">{{ symbol }}</span>
        }
      </p>
    }

    @if (rows().length > 1) {
      <p class="hint">{{ t('humanDesign.iching.reading.oneByOne') }}</p>
    }
  `,
})
export class HdIChingMovingLinesComponent {
  readonly reading = input.required<IChingReading>();
  readonly method = input.required<IChingMethodContent>();
  /** Muntzijden per worp (index 0 = lijn 1); leeg zonder muntworp. */
  readonly coinFaces = input<ReadonlyArray<readonly IChingCoinFace[]>>([]);

  protected readonly t = injectIChingTranslate();

  /** Bewegende lijnen oplopend (de onderste eerst), met de waarschuwing die bij yang of yin hoort. */
  protected readonly rows = computed<IChingMovingRow[]>(() =>
    buildMovingRows(this.reading(), this.method(), this.coinFaces(), this.t),
  );

  protected readonly summarySymbols = computed(() =>
    this.reading().movingLineTrigrams.map(id => ICHING_TRIGRAMS[id].symbol),
  );
}
